import { IconEye } from '@tabler/icons-react';
import { formatDate } from '@/lib/utils';
import {
  type ColumnConfig,
  DataTable,
  type DataTableResponse,
  type FilterConfig,
} from './data-table';
import type { RowActivatorLink } from './row-activator';
import { ClientStatus, StatusChip } from './status-chip';
import { Button } from './ui/button';

export interface ClientRow {
  id: string;
  name: string;
  clientCode?: string | null;
  email?: string | null;
  status?: string;
  createdAt?: string;
  primaryContactName?: string | null;
  primaryContactPhone?: string | null;
}

interface Props {
  data?: DataTableResponse<ClientRow> | undefined;
  isLoading?: boolean;
  error?: string | null;
  onClientClick?: ((client: ClientRow) => void) | undefined;
  /** Detail route for the row's keyboard activator; omit to fall back to a button. */
  getRowLink?: ((client: ClientRow) => RowActivatorLink) | undefined;
  onQuickView?: ((client: ClientRow) => void) | undefined;
  onFilterChange?: (filterKey: string, value: string | string[]) => void;
  onSortChange?: (sortBy: string, order: 'asc' | 'desc') => void;
  onPageChange?: (page: number) => void;
  searchInput?: React.ReactNode;
  /** Sort actually in effect, so the header arrow reflects the query. */
  currentSortKey?: string;
  currentSortOrder?: 'asc' | 'desc';
}

export const ClientsTable = ({
  data,
  isLoading = false,
  error = null,
  onClientClick,
  getRowLink,
  onQuickView,
  onFilterChange,
  onSortChange,
  onPageChange,
  searchInput,
  currentSortKey,
  currentSortOrder,
}: Props) => {
  const columns: ColumnConfig<ClientRow>[] = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (item) => <span className="font-medium">{item.name}</span>,
    },
    {
      key: 'client_code',
      label: 'Client Code',
      sortable: true,
      render: (item) => (
        <span className="font-mono text-muted-foreground">{item.clientCode || '—'}</span>
      ),
    },
    {
      key: 'company_email',
      label: 'Email',
      sortable: true,
      render: (item) => <span className="text-muted-foreground">{item.email || '—'}</span>,
    },
    {
      key: 'primary_contact_name',
      label: 'Primary Contact',
      sortable: true,
      render: (item) => (
        <span className="text-muted-foreground">{item.primaryContactName || '—'}</span>
      ),
    },
    {
      key: 'primary_contact_phone',
      label: 'Contact Phone',
      sortable: true,
      render: (item) => (
        <span className="text-muted-foreground">{item.primaryContactPhone || '—'}</span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (item) => {
        const toClientStatus = (value?: string): ClientStatus => {
          switch (value) {
            case ClientStatus.INACTIVE:
              return ClientStatus.INACTIVE;
            case ClientStatus.PENDING_VERIFICATION:
              return ClientStatus.PENDING_VERIFICATION;
            case ClientStatus.ACTIVE:
              return ClientStatus.ACTIVE;
            case ClientStatus.SUSPENDED:
              return ClientStatus.SUSPENDED;
            default:
              return ClientStatus.ACTIVE;
          }
        };
        return <StatusChip status={toClientStatus(item.status)} type="client" />;
      },
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      align: 'right',
      render: (item) => (
        <span className="text-muted-foreground">
          {item.createdAt ? formatDate(item.createdAt) : '—'}
        </span>
      ),
    },
    ...(onQuickView
      ? [
          {
            key: 'actions',
            label: 'Actions',
            align: 'right' as const,
            render: (item: ClientRow) => (
              <div className="flex justify-end">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground"
                  onClick={(e) => {
                    e.stopPropagation();
                    onQuickView(item);
                  }}
                  title="Quick view"
                  aria-label={`Quick view ${item.name}`}
                >
                  <IconEye className="h-4 w-4" aria-hidden />
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ];

  const filters: FilterConfig[] = [
    {
      key: 'status',
      label: 'Status',
      placeholder: 'Select status',
      multiSelect: false,
      options: [
        { value: 'ACTIVE', label: 'Active' },
        { value: 'INACTIVE', label: 'Inactive' },
        { value: 'PENDING_VERIFICATION', label: 'Pending Verification' },
        { value: 'SUSPENDED', label: 'Suspended' },
      ],
    },
  ];

  return (
    <DataTable
      data={data}
      columns={columns}
      caption="Clients"
      filters={filters}
      isLoading={isLoading}
      error={error}
      onRowClick={onClientClick}
      getRowLabel={(item) => `Open client ${item.name}`}
      {...(getRowLink && { getRowLink })}
      {...(onFilterChange && { onFilterChange })}
      {...(onSortChange && { onSortChange })}
      {...(onPageChange && { onPageChange })}
      extraFilters={searchInput}
      emptyMessage="No clients found"
      loadingMessage="Loading clients..."
      // Driven by the page's filter state rather than a default declared here:
      // the two disagreed, so the arrow sat on Name asc while the API returned
      // created_at desc, and a shared ?sortBy= link showed the wrong column.
      {...(currentSortKey && { currentSortKey })}
      {...(currentSortOrder && { currentSortOrder })}
    />
  );
};
